import { Component, computed, effect, ElementRef, inject, signal, viewChild } from '@angular/core';
import {
  ButtonDirective,
  ConfirmationDialogComponent,
  CuiDialogService,
  EmptyStateComponent,
} from '@innersource/common-ui-components';
import { ChatInputComponent } from './chat-input/chat-input.component';
import { ChatHistoryComponent } from './chat-history/chat-history.component';
import { ChatSessionHistoryComponent } from './chat-session-history/chat-session-history.component';
import { ChatHeaderComponent } from './chat-header/chat-header.component';
import { ChatExpertPickerComponent } from './chat-expert-picker/chat-expert-picker.component';
import { ChatAttachmentsService } from './chat-attachments.service';
import { ChatConversationService } from './chat-conversation.service';
import { ChatSession } from './chat.model';
import { ChatService } from './chat.service';
import { ChatStore } from './chat.store';
import { ChatWindowService } from './chat-window.service';

/** Arranges the session list, conversation, and composer for each chat layout. */
@Component({
  selector: 'app-chat',
  imports: [
    ChatInputComponent,
    ChatHistoryComponent,
    ChatSessionHistoryComponent,
    ChatHeaderComponent,
    ChatExpertPickerComponent,
    ButtonDirective,
    EmptyStateComponent,
  ],
  providers: [ChatService, ChatStore, ChatConversationService, ChatAttachmentsService],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss',
  standalone: true,
  host: {
    '[class.chat--hidden]': 'chatWindow.mode() === "hidden"',
    '[class.chat--fullscreen]': 'chatWindow.mode() === "fullscreen"',
    '(dragenter)': 'onDragEnter($event)',
    '(dragover)': 'onDragOver($event)',
    '(dragleave)': 'onDragLeave()',
    '(drop)': 'onDrop($event)',
  },
})
export class ChatComponent {
  /** Services. */
  readonly conversation = inject(ChatConversationService);
  readonly chatWindow = inject(ChatWindowService);
  readonly attachments = inject(ChatAttachmentsService);
  private readonly dialog = inject(CuiDialogService);

  /** State. */
  readonly showScrollButton = signal(false);
  readonly inputFocused = signal(false);
  readonly draggingFiles = signal(false);
  private dragDepth = 0;
  private readonly chatInput = viewChild(ChatInputComponent);
  private readonly chatMain = viewChild<ElementRef<HTMLElement>>('chatMain');

  /** Shows the start prompt until the conversation has a message. */
  readonly isStartView = computed(
    () =>
      !this.conversation.loading() &&
      this.conversation.messages().length === 0 &&
      !this.conversation.currentAgentMsg() &&
      !this.conversation.isRunPending()
  );

  /** Shows the session list at the top of the sidebar overview. */
  readonly showOverviewHistory = computed(
    () =>
      this.isStartView() &&
      this.chatWindow.mode() === 'sidebar' &&
      !this.conversation.creating() &&
      !this.inputFocused() &&
      !this.conversation.activeSession() &&
      this.conversation.sessions().length > 0
  );

  /** Keeps recent responses visible while the user is near the bottom. */
  constructor() {
    // Scroll when saved or streaming messages change and the reader is near the bottom.
    effect(() => {
      this.conversation.messages();
      const streamingMessage = this.conversation.currentAgentMsg();
      const element = this.chatMain()?.nativeElement;
      if (!element) return;
      const distance = element.scrollHeight - element.scrollTop - element.clientHeight;
      if (distance <= 150 || streamingMessage) this.scrollToBottom('auto');
    });
  }

  /** Opens a saved conversation and shows its newest messages. */
  async openSession(session: ChatSession): Promise<void> {
    this.attachments.clear();
    await this.conversation.openSession(session);
    this.scrollToBottom('instant');
  }

  /** Shows the chat overview without focusing the composer. */
  showOverview(): void {
    this.attachments.clear();
    this.conversation.newChat();
    this.showScrollButton.set(false);
    this.inputFocused.set(false);
  }

  /** Starts a new conversation and focuses the composer. */
  newChat(): void {
    this.showOverview();
    this.inputFocused.set(true);
    queueMicrotask(() => this.chatInput()?.focus());
  }

  /** Changes the expert used for the next message. */
  selectExpert(expertId: string): void {
    this.conversation.selectExpert(expertId);
  }

  /** Confirms the removal of a saved conversation. */
  requestDeleteSession(session: ChatSession): void {
    const title = session.title || 'New chat';
    this.dialog.open(ConfirmationDialogComponent, {
      size: 'small',
      ariaLabel: 'Delete Chat',
      data: {
        confirmTitle: 'Delete Chat',
        confirmMessage: `Delete “${title}”? This cannot be undone.`,
        confirmLabel: 'Delete',
        onConfirm: () => this.conversation.deleteSession(session),
      },
    });
  }

  /** Sends a prompt and scrolls to the new message. */
  async sendMessage(text: string): Promise<void> {
    if (this.attachments.uploading()) return;
    const files = this.attachments.pending();
    const sent = await this.conversation.sendMessage(text, files);
    if (sent) this.attachments.markSent(files);
    this.scrollToBottom();
  }

  /** Shows the drop target while files are dragged over the chat. */
  onDragEnter(event: DragEvent): void {
    if (!this.hasDraggedFiles(event)) return;
    event.preventDefault();
    this.dragDepth++;
    this.draggingFiles.set(true);
  }

  /** Allows the browser to drop files anywhere in the chat area. */
  onDragOver(event: DragEvent): void {
    if (!this.hasDraggedFiles(event)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  /** Hides the target once the drag leaves the whole chat area. */
  onDragLeave(): void {
    if (!this.draggingFiles()) return;
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (!this.dragDepth) this.draggingFiles.set(false);
  }

  /** Uploads files dropped on the chat area. */
  onDrop(event: DragEvent): void {
    if (!this.hasDraggedFiles(event)) return;
    event.preventDefault();
    this.dragDepth = 0;
    this.draggingFiles.set(false);
    const files = Array.from(event.dataTransfer?.files ?? []);
    void this.attachments.addFiles(files);
  }

  /** Checks whether a drag carries local files. */
  private hasDraggedFiles(event: DragEvent): boolean {
    return this.chatWindow.mode() !== 'hidden' && !!event.dataTransfer?.types.includes('Files');
  }

  /** Updates the scroll-to-bottom control. */
  onScroll(): void {
    const element = this.chatMain()?.nativeElement;
    if (!element) return;
    const distance = element.scrollHeight - element.scrollTop - element.clientHeight;
    this.showScrollButton.set(distance > 100);
  }

  /** Scrolls the conversation to its newest message. */
  scrollToBottom(behavior: ScrollBehavior = 'smooth'): void {
    requestAnimationFrame(() => {
      const element = this.chatMain()?.nativeElement;
      element?.scrollTo({ top: element.scrollHeight, behavior });
    });
  }
}
